import * as Sentry from '@sentry/react'
import { useCallback, useMemo } from 'react'
import { useLocation } from 'react-router-dom'

import { useAuth } from '@affiliate-cabinet/auth'
import { useI18n } from '@affiliate-cabinet/i18n'
import { useQueryClient } from '@affiliate-cabinet/rest'
import { useWindowScroll } from '@affstore-ds/helpers'
import { MainLayoutPropertiesType, useNavbarState } from '@affstore-ds/uikit'
import { DateTime } from 'luxon'

import { getAvatarSource, getManagerWorkSchedule, mapManagerStatusToAvatarElementVariant } from '../../../../../entities/manager'
import { clearLocalStorageOnLogout } from '../../../../../shared/localStorage'
import { NAV_MENU_DATA } from '../../../../../shared/navigation/NavMenuData'
import { getNavMenuItemDataTestId } from '../../../../../shared/navigation/navMenuDataTestIds'
import { useNavigationContainer } from '../../../../../shared/providers'
import { routes } from '../../../../../shared/routes'
import { buttonCopyTranslationKeys } from '../../../../../shared/translations/buttonCopy.translationKeys'
import { commonTranslationKeys } from '../../../../../shared/translations/common.translationKeys'
import { getOfferLinkPathParameters, OFFER_FROM_URL_PARAM, OnboardingToOfferConversionFunnel } from '../../../../../shared/utils'
import { navMenuButtonLogoutTranslationKeys, navMenuManagerInfoTranslationKeys } from '../translations'

const isOfferIdPathname = (pathname?: string) =>
  pathname?.startsWith(routes.ROUTES_OFFERS.offers) && !pathname?.startsWith(routes.ROUTES_OFFERS.offersAdvertisers)

export const useNavBar = (isOnboarding: boolean): MainLayoutPropertiesType['navbarProps'] => {
  const {
    auth: { isUserLoading, signOut, user, userRefetch },
  } = useAuth()

  const queryClient = useQueryClient()

  const { i18n } = useI18n()

  const { redirect } = useNavigationContainer()

  const [, scrollTo] = useWindowScroll()

  const location = useLocation()

  const { closeMobileNavbar } = useNavbarState()

  const activeLink = useMemo(
    () =>
      location.search.includes(OFFER_FROM_URL_PARAM) || isOfferIdPathname(location.pathname)
        ? getOfferLinkPathParameters(location.search)
        : location.pathname,
    [location.pathname, location.search],
  )

  const handleLinkClick = useCallback(
    (nextPath: string | undefined = '') => {
      if (!nextPath) {
        return
      }
      scrollTo({ y: 0 })
      redirect(nextPath)
      closeMobileNavbar()

      if (!isUserLoading) {
        userRefetch()
      }
    },
    [scrollTo, redirect, isUserLoading, closeMobileNavbar, userRefetch],
  )

  const managerInfo = useMemo<MainLayoutPropertiesType['navbarProps']['managerInfo']>(() => {
    const manager = user?.manager

    if (!manager || isOnboarding) {
      return undefined
    }

    return {
      labels: {
        contactsSectionLabel: i18n.t(navMenuManagerInfoTranslationKeys.navMenuManagersContacts),
        copyTooltipText: i18n.t(buttonCopyTranslationKeys.tooltipCopied),
        sendMessageLabel: i18n.t(navMenuManagerInfoTranslationKeys.navMenuButtonSendMessage),
      },
      avatarVariant: mapManagerStatusToAvatarElementVariant(manager),
      contacts: manager.alt_contact || [],
      firstName: manager.first_name || '',
      lastName: manager.last_name || '',
      photo: manager.photo ? getAvatarSource(manager.photo) : '',
      work: getManagerWorkSchedule({ i18n, workingHours: manager.working_hours }),
    }
  }, [user?.manager, i18n, isOnboarding])

  const getLinkNewBadge = useCallback(
    (counter?: number | string) => {
      if (!counter) {
        return
      }

      return (Number(counter) || 0) > DateTime.now().toMillis() ? i18n.t(commonTranslationKeys.commonNew) : undefined
    },
    [i18n],
  )

  const links = useMemo(
    () =>
      NAV_MENU_DATA.map(({ dataTestId, ...link }) => ({
        ...link,
        label: i18n.t(link.label),
        children: link.children?.map(({ dataTestId, ...child }) => ({
          ...child,
          label: i18n.t(child.label),
          counter: getLinkNewBadge(child.counter),
          dataTestId: getNavMenuItemDataTestId(dataTestId),
          disabled: isOnboarding,
        })),
        counter: getLinkNewBadge(link.counter),
        dataTestId: getNavMenuItemDataTestId(dataTestId),
        disabled: isOnboarding,
      })),
    [i18n, getLinkNewBadge, isOnboarding],
  )

  const handleSignOut = useCallback(() => {
    Sentry.setUser(null)
    // logoutFromSurvey()
    clearLocalStorageOnLogout()
    queryClient.clear()
    OnboardingToOfferConversionFunnel.clearCookies()
    signOut()
  }, [queryClient, signOut])

  return {
    activeLink,
    links,
    logout: {
      logoutButtonText: i18n.t(navMenuButtonLogoutTranslationKeys.navMenuButtonLogout),
      onLogoutClick: handleSignOut,
    },
    managerInfo,
    onLinkClick: handleLinkClick,
    withCollapse: false,
  }
}
